import {assert} from 'Asserts';
import {Notation} from 'biojs/BioConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {SequenceSchemaType, TagSchema} from 'constants/RegistryConstants';
import {RegistrationStructuredTableMetadata} from 'notebook/integrations/registry/RegistrationTableConstants';
import {SMILES_COLUMN_NAME} from 'notebook/registration-tables/RegistrationTableUtil';
import {EXISTING_BIOENTITY_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  BasesStructuredTableColumn,
  SchemaAndColumnGroup,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import {entityTypeSupportsChemicalStructure} from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';

import {
  BasesColumnConfig,
  BasesColumnEntityType,
  getBasesColumnConfigDisplayName,
} from '../bases-columns/BasesColumnConstants';

function getBasesColumn<TTableMetadata>(
  schema: TagSchema,
  metadata?: TTableMetadata
): Array<StructuredTableColumn> {
  const maybeRegTableMetadata = metadata as unknown as RegistrationStructuredTableMetadata | undefined;
  const notation = maybeRegTableMetadata?.notation ?? Notation.NATURAL_BASES;
  let customSyntaxName;
  if (notation === Notation.CUSTOM_SYNTAX) {
    assert(
      Boolean(maybeRegTableMetadata?.customSyntaxName),
      'Expected metadata.customSyntaxName to be defined'
    );
    customSyntaxName = maybeRegTableMetadata!.customSyntaxName;
  }
  let basesColumnConfig: BasesColumnConfig | null = null;
  switch (schema.sequenceType ?? schema.folderItemType) {
    case SequenceSchemaType.OLIGO:
      basesColumnConfig = {
        entityType: BasesColumnEntityType.DNA_OLIGO,
        syntax: notation,
        customSyntaxName,
      };
      break;
    case SequenceSchemaType.RNA_OLIGO:
      basesColumnConfig = {
        entityType: BasesColumnEntityType.RNA_OLIGO,
        syntax: notation,
        customSyntaxName,
      };
      break;
    case SequenceSchemaType.DNA_SEQUENCE:
      basesColumnConfig = {
        entityType: BasesColumnEntityType.DNA_SEQUENCE,
        syntax: notation,
      };
      break;
    case SequenceSchemaType.RNA_SEQUENCE:
      basesColumnConfig = {
        entityType: BasesColumnEntityType.RNA_SEQUENCE,
        syntax: notation,
        customSyntaxName,
      };
      break;
    case ModelTypeConstants.PROTEIN:
      basesColumnConfig = {
        entityType: BasesColumnEntityType.AA_SEQUENCE,
      };
      break;
    default:
      return [];
  }

  assert(basesColumnConfig != null, 'Expected basesColumnConfig to be set for BASES column type');
  const columnName = getBasesColumnConfigDisplayName(basesColumnConfig);
  const column = makeStructuredTableColumn({columnType: TableColumnType.BASES, name: columnName});
  const additionalColumns =
    schema.sequenceType === SequenceSchemaType.DNA_SEQUENCE
      ? [
          makeStructuredTableColumn({
            columnType: TableColumnType.SEQUENCE_TOPOLOGY,
            name: 'Topology',
          }),
        ]
      : [];
  return [{...column, basesColumnConfig} as BasesStructuredTableColumn, ...additionalColumns];
}

function getChemicalStructureColumn(schema: TagSchema): Array<StructuredTableColumn> {
  if (entityTypeSupportsChemicalStructure(schema.folderItemType)) {
    return [makeStructuredTableColumn({columnType: TableColumnType.SMILES, name: SMILES_COLUMN_NAME})];
  } else {
    return [];
  }
}

function getSchemaSpecificBioentityColumns<TTableMetadata>(
  schema: TagSchema,
  metadata?: TTableMetadata
): Array<StructuredTableColumn> {
  return [...getBasesColumn(schema, metadata), ...getChemicalStructureColumn(schema)];
}

function getBioentityColumns<TTableMetadata>(
  schemas: Array<SchemaAndColumnGroup>,
  metadata?: TTableMetadata
): Array<StructuredTableColumn> {
  return schemas
    .filter((schema) => APIUtil.isTagSchema(schema.id))
    .flatMap((schema) => {
      const bioentityColumn = makeStructuredTableColumn({
        columnType: TableColumnType.EXISTING_BIOENTITY,
        name: EXISTING_BIOENTITY_COLUMN_NAME,
        columnGroup: schema.columnGroup,
      });
      return [
        bioentityColumn,
        ...getSchemaSpecificBioentityColumns(schema as unknown as TagSchema, metadata),
      ];
    });
}

function getSchemaFieldColumns(
  schemas: Array<SchemaAndColumnGroup>,
  selectorsById: {[selectorId: string]: SchemaFieldSelector}
): Array<StructuredTableColumn> {
  return schemas.flatMap((schema) =>
    schema.allFields
      .filter((field) => field.archiveRecord == null)
      .map((schemaField) =>
        makeSchemaFieldStructuredColumn(schemaField, selectorsById, schema.columnGroup, schema.allFields)
      )
  );
}

/**
 * This function generates an array of StructuredTableColumns needed
 * to express a table for a particular entry type (determined by the
 * entryId) and an array of Schemas.
 *
 * @param entryId - the api identifier of the entry which allows us to determine if it's a StageEntry or Entry
 * @param schemas - an array of Schemas as well as the `group` that helps distinguish between multiple
 *                  schemas with the same id
 * @param selectorsById - a map of SchemaFieldSelectors
 * @param metadata - the metadata for a structured table, if any
 */
export default function toStructuredTableColumns<TTableMetadata>({
  schemas,
  selectorsById,
  metadata,
}: {
  schemas: Array<SchemaAndColumnGroup>;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
  metadata?: TTableMetadata;
}): Array<StructuredTableColumn> {
  return [getBioentityColumns(schemas, metadata), getSchemaFieldColumns(schemas, selectorsById)].flat(1);
}
